import React, { useMemo, useState } from "react";
import { FiCloud, FiExternalLink, FiRefreshCw } from "react-icons/fi";
import {
	useAnalyticsOverview,
	type InstanceSummary,
	type CountryTraffic,
	type AnalyticsBreakdownItem,
	type PageTraffic,
	type TrafficPoint,
} from "../hooks/useAnalytics";
import { WorldTrafficMap } from "../Components/dashboard/WorldTrafficMap";
import { flagEmoji, countryName } from "../Components/dashboard/geo/mapMath";
import { PagesInsights, TrendChip } from "../Components/analytics/PagesInsights";
import { TrafficSources } from "../Components/analytics/TrafficSources";
import { mergePages, mergeReferrers, trendOf } from "../Components/analytics/pagesInsightsModel";

/**
 * Web Analytics : the full standalone audience dashboard for the public sites, powered by Cloudflare
 * Web Analytics (RUM): cookieless, bot-filtered, real browser traffic. A site selector (all sites /
 * production / preview) and a 7/30/90-day window drive every panel: KPI tiles (with the trend against
 * the previous window), a combined visits + page-views chart, the most viewed pages (per page or per
 * section, with sparklines and trends), the traffic sources, a visitor map with a country leaderboard,
 * and browser / OS / device breakdowns. One request to `/api/analytics/overview?days=N`; "all sites"
 * aggregates the instances client-side.
 */

const compact = new Intl.NumberFormat("fr-FR", { notation: "compact", maximumFractionDigits: 1 });
const full = new Intl.NumberFormat("fr-FR");
const dayMonthFmt = new Intl.DateTimeFormat("fr-FR", { day: "2-digit", month: "2-digit" });
const fullDateFmt = new Intl.DateTimeFormat("fr-FR", { weekday: "long", day: "numeric", month: "long" });

const VISITS_COLOR = "#F59E0B"; // amber
const VIEWS_COLOR = "#6366F1"; // indigo

const TARGET_LABEL: Record<string, string> = { production: "Production", preview: "Preview", all: "Tous les sites" };

/** Parse an ISO yyyy-MM-dd as a LOCAL date so the rendered day never shifts by a timezone offset. */
const parseLocalDay = (iso: string): Date => {
	const [y, m, d] = (iso || "").split("-").map(Number);
	return new Date(y || 1970, (m || 1) - 1, d || 1);
};

/** A merged, ready-to-render view of one or more instances (when "all sites" is picked). */
type AnalyticsView = {
	configured: boolean;
	reason?: string | null;
	visits: number;
	pageViews: number;
	previousVisits: number;
	previousPageViews: number;
	series: TrafficPoint[];
	countries: CountryTraffic[];
	browsers: AnalyticsBreakdownItem[];
	operatingSystems: AnalyticsBreakdownItem[];
	devices: AnalyticsBreakdownItem[];
	pages: PageTraffic[];
	/** Distinct pages seen over the window (uncapped; summed over the selected instances). */
	pageCount: number;
	referrers: AnalyticsBreakdownItem[];
	/** Canonical public host of the selection (production's when "all"), to open a page. */
	host: string | null;
};

const sortByCount = <T extends { count: number }>(rows: T[]) => [...rows].sort((a, b) => b.count - a.count);
const sortByViews = (rows: AnalyticsBreakdownItem[]) => [...rows].sort((a, b) => b.pageViews - a.pageViews);

/** Sum a list of breakdown slices by label (case-insensitive), busiest-first. */
function mergeBreakdown(lists: AnalyticsBreakdownItem[][]): AnalyticsBreakdownItem[] {
	const byLabel = new Map<string, AnalyticsBreakdownItem>();
	for (const list of lists) {
		for (const it of list || []) {
			const key = (it.label || "").toLowerCase();
			const prev = byLabel.get(key);
			if (prev) {
				prev.pageViews += it.pageViews;
				prev.visits += it.visits;
			} else {
				byLabel.set(key, { label: it.label, pageViews: it.pageViews, visits: it.visits });
			}
		}
	}
	return sortByViews([...byLabel.values()]);
}

/** Collapse the selected instances into a single view: a single instance passes through; "all" sums. */
function buildView(instances: InstanceSummary[]): AnalyticsView {
	const usable = instances.filter((i) => i.configured);
	if (usable.length === 0) {
		const first = instances[0];
		return {
			configured: false,
			reason: first?.reason,
			visits: 0,
			pageViews: 0,
			previousVisits: 0,
			previousPageViews: 0,
			series: [],
			countries: [],
			browsers: [],
			operatingSystems: [],
			devices: [],
			pages: [],
			pageCount: 0,
			referrers: [],
			host: null,
		};
	}

	// Series: sum visits + page views per date across instances.
	const seriesByDate = new Map<string, TrafficPoint>();
	for (const inst of usable) {
		for (const p of inst.series || []) {
			const prev = seriesByDate.get(p.date);
			if (prev) {
				prev.visits += p.visits;
				prev.pageViews += p.pageViews;
			} else {
				seriesByDate.set(p.date, { date: p.date, visits: p.visits, pageViews: p.pageViews });
			}
		}
	}

	// Countries: sum page views per alpha-2 code.
	const countryByCode = new Map<string, CountryTraffic>();
	for (const inst of usable) {
		for (const c of inst.countries || []) {
			const prev = countryByCode.get(c.code);
			if (prev) prev.count += c.count;
			else countryByCode.set(c.code, { code: c.code, count: c.count });
		}
	}

	// The host a page opens on: production's canonical host when it is part of the selection.
	const canonical = usable.find((i) => i.target === "production") ?? usable[0];

	return {
		configured: true,
		visits: usable.reduce((s, i) => s + i.visits, 0),
		pageViews: usable.reduce((s, i) => s + i.pageViews, 0),
		previousVisits: usable.reduce((s, i) => s + (i.previousVisits ?? 0), 0),
		previousPageViews: usable.reduce((s, i) => s + (i.previousPageViews ?? 0), 0),
		series: [...seriesByDate.values()].sort((a, b) => a.date.localeCompare(b.date)),
		countries: sortByCount([...countryByCode.values()]),
		browsers: mergeBreakdown(usable.map((i) => i.browsers)),
		operatingSystems: mergeBreakdown(usable.map((i) => i.operatingSystems)),
		devices: mergeBreakdown(usable.map((i) => i.devices)),
		pages: mergePages(usable.map((i) => i.pages)),
		pageCount: usable.reduce((s, i) => s + (i.pageCount ?? i.pages?.length ?? 0), 0),
		referrers: mergeReferrers(usable.map((i) => i.referrers)),
		host: canonical?.host ?? null,
	};
}

/** A bordered white panel with an uppercase tracking-wide title : the mockup's card shell. */
const Panel: React.FC<{ title?: string; className?: string; children: React.ReactNode }> = ({ title, className, children }) => (
	<div className={`rounded-xl border border-slate-200 bg-white shadow-sm ${className ?? ""}`}>
		{title ? <div className="px-5 pt-4 text-[11px] font-semibold uppercase tracking-wide text-slate-400">{title}</div> : null}
		{children}
	</div>
);

const Kpi: React.FC<{ label: string; value: string; color: string; hint?: string; trend?: { current: number; previous: number } | null }> = ({ label, value, color, hint, trend }) => (
	<Panel className="px-5 py-4">
		<p className="text-[11px] font-semibold uppercase tracking-wide text-slate-400">{label}</p>
		<div className="mt-1 flex flex-wrap items-baseline gap-2">
			<p className="text-3xl font-bold tabular-nums tracking-tight" style={{ color }}>
				{value}
			</p>
			{trend ? <TrendChip trend={trendOf(trend.current, trend.previous)} /> : null}
		</div>
		{hint ? <p className="mt-0.5 text-[11px] text-slate-400">{hint}</p> : null}
	</Panel>
);

/** Combined VISITS + PAGE VIEWS area chart (two overlaid smoothed areas + a per-day hover dot). */
const DualAreaChart: React.FC<{ series: TrafficPoint[] }> = ({ series }) => {
	const W = 1000;
	const H = 320;
	const padL = 36;
	const padR = 16;
	const padT = 16;
	const padB = 34;
	const innerW = W - padL - padR;
	const innerH = H - padT - padB;

	if (series.length < 2) {
		return <div className="flex h-72 items-center justify-center text-xs text-slate-400">L'historique se remplit (Web Analytics activé récemment).</div>;
	}

	const maxVal = Math.max(1, ...series.map((p) => Math.max(p.visits, p.pageViews)));
	// Nice ceiling + ticks (0, ¼, ½, ¾, max) rounded to a readable step.
	const niceTop = (() => {
		const raw = maxVal;
		const mag = Math.pow(10, Math.floor(Math.log10(raw)));
		const n = raw / mag;
		const step = n <= 1 ? 1 : n <= 2 ? 2 : n <= 5 ? 5 : 10;
		return step * mag;
	})();
	const ticks = [0, 0.25, 0.5, 0.75, 1].map((f) => Math.round(niceTop * f));

	const x = (i: number) => padL + (series.length === 1 ? innerW / 2 : (i / (series.length - 1)) * innerW);
	const y = (v: number) => padT + innerH - (v / niceTop) * innerH;

	// Smooth path via Catmull-Rom → cubic Bézier.
	const smoothLine = (pts: Array<[number, number]>): string => {
		if (pts.length < 2) return "";
		let d = `M ${pts[0][0]},${pts[0][1]}`;
		for (let i = 0; i < pts.length - 1; i++) {
			const p0 = pts[i - 1] || pts[i];
			const p1 = pts[i];
			const p2 = pts[i + 1];
			const p3 = pts[i + 2] || p2;
			const c1x = p1[0] + (p2[0] - p0[0]) / 6;
			const c1y = p1[1] + (p2[1] - p0[1]) / 6;
			const c2x = p2[0] - (p3[0] - p1[0]) / 6;
			const c2y = p2[1] - (p3[1] - p1[1]) / 6;
			d += ` C ${c1x},${c1y} ${c2x},${c2y} ${p2[0]},${p2[1]}`;
		}
		return d;
	};

	const viewsPts = series.map((p, i) => [x(i), y(p.pageViews)] as [number, number]);
	const visitsPts = series.map((p, i) => [x(i), y(p.visits)] as [number, number]);
	const baseline = padT + innerH;
	const areaFrom = (line: string, pts: Array<[number, number]>) => `${line} L ${pts[pts.length - 1][0]},${baseline} L ${pts[0][0]},${baseline} Z`;
	const viewsLine = smoothLine(viewsPts);
	const visitsLine = smoothLine(visitsPts);

	const labelStride = series.length > 8 ? Math.ceil(series.length / 7) : 1;

	return (
		<svg viewBox={`0 0 ${W} ${H}`} className="h-72 w-full" role="img" aria-label="Visites et pages vues par jour">
			<defs>
				<linearGradient id="aViews" x1="0" y1="0" x2="0" y2="1">
					<stop offset="0%" stopColor={VIEWS_COLOR} stopOpacity="0.22" />
					<stop offset="100%" stopColor={VIEWS_COLOR} stopOpacity="0" />
				</linearGradient>
				<linearGradient id="aVisits" x1="0" y1="0" x2="0" y2="1">
					<stop offset="0%" stopColor={VISITS_COLOR} stopOpacity="0.20" />
					<stop offset="100%" stopColor={VISITS_COLOR} stopOpacity="0" />
				</linearGradient>
			</defs>

			{/* gridlines + y labels */}
			{ticks.map((t) => (
				<g key={t}>
					<line x1={padL} y1={y(t)} x2={W - padR} y2={y(t)} stroke="#EEF2F7" strokeWidth={1} />
					<text x={padL - 8} y={y(t) + 3} textAnchor="end" fontSize="11" fill="#94A3B8">
						{compact.format(t)}
					</text>
				</g>
			))}

			{/* areas (views behind, visits in front) */}
			<path d={areaFrom(viewsLine, viewsPts)} fill="url(#aViews)" />
			<path d={areaFrom(visitsLine, visitsPts)} fill="url(#aVisits)" />
			<path d={viewsLine} fill="none" stroke={VIEWS_COLOR} strokeWidth={2.5} />
			<path d={visitsLine} fill="none" stroke={VISITS_COLOR} strokeWidth={2.5} />

			{/* per-day dots with native hover tooltip */}
			{series.map((p, i) => (
				<g key={p.date}>
					<circle cx={x(i)} cy={y(p.pageViews)} r={2.4} fill={VIEWS_COLOR} />
					<circle cx={x(i)} cy={y(p.visits)} r={2.4} fill={VISITS_COLOR} />
					<rect x={x(i) - 14} y={padT} width={28} height={innerH} fill="transparent">
						<title>{`${fullDateFmt.format(parseLocalDay(p.date))} · ${full.format(p.visits)} visites · ${full.format(p.pageViews)} pages vues`}</title>
					</rect>
					{i % labelStride === 0 || i === series.length - 1 ? (
						<text x={x(i)} y={H - 12} textAnchor="middle" fontSize="11" fill="#94A3B8">
							{dayMonthFmt.format(parseLocalDay(p.date))}
						</text>
					) : null}
				</g>
			))}
		</svg>
	);
};

const Legend: React.FC = () => (
	<div className="flex items-center gap-4 text-xs font-medium text-slate-500">
		<span className="inline-flex items-center gap-1.5">
			<span className="h-2.5 w-2.5 rounded-full" style={{ background: VISITS_COLOR }} /> Visites
		</span>
		<span className="inline-flex items-center gap-1.5">
			<span className="h-2.5 w-2.5 rounded-full" style={{ background: VIEWS_COLOR }} /> Pages vues
		</span>
	</div>
);

/** Country leaderboard row: flag, count, share bar. */
const CountryRow: React.FC<{ country: CountryTraffic; max: number }> = ({ country, max }) => {
	const pct = max > 0 ? Math.max(2, Math.round((country.count / max) * 100)) : 0;
	return (
		<li className="py-1.5">
			<div className="flex items-center gap-2.5">
				<span className="w-5 shrink-0 text-center text-base leading-none" aria-hidden>
					{flagEmoji(country.code) || "🏳️"}
				</span>
				<span className="flex-1 truncate text-[13px] font-medium text-slate-700" title={countryName(country.code, "fr")}>
					{country.code}
				</span>
				<span className="shrink-0 text-[12px] font-semibold tabular-nums text-slate-500" title={full.format(country.count)}>
					{compact.format(country.count)}
				</span>
			</div>
			<div className="mt-1 ml-7 h-1.5 overflow-hidden rounded-full bg-slate-100">
				<div className="h-full rounded-full" style={{ width: `${pct}%`, background: VIEWS_COLOR }} />
			</div>
		</li>
	);
};

/** A breakdown panel (browsers / OS / devices): label + count + share bar, busiest-first. */
const BreakdownPanel: React.FC<{ title: string; items: AnalyticsBreakdownItem[] }> = ({ title, items }) => {
	const rows = items.slice(0, 6);
	const max = rows.reduce((m, r) => Math.max(m, r.pageViews), 0);
	return (
		<Panel title={title} className="p-0">
			<div className="px-5 pb-4 pt-2">
				{rows.length === 0 ? (
					<p className="py-6 text-center text-xs text-slate-400">Aucune donnée pour cette période.</p>
				) : (
					<ul className="space-y-2.5">
						{rows.map((r) => {
							const pct = max > 0 ? Math.max(2, Math.round((r.pageViews / max) * 100)) : 0;
							return (
								<li key={r.label}>
									<div className="flex items-center justify-between gap-2 text-[13px]">
										<span className="truncate font-medium text-slate-700">{r.label}</span>
										<span className="shrink-0 font-semibold tabular-nums text-slate-500" title={full.format(r.pageViews)}>
											{compact.format(r.pageViews)}
										</span>
									</div>
									<div className="mt-1 h-1.5 overflow-hidden rounded-full bg-slate-100">
										<div className="h-full rounded-full" style={{ width: `${pct}%`, background: VIEWS_COLOR }} />
									</div>
								</li>
							);
						})}
					</ul>
				)}
			</div>
		</Panel>
	);
};

const WINDOWS: Array<{ days: number; label: string }> = [
	{ days: 7, label: "7d" },
	{ days: 30, label: "30d" },
	{ days: 90, label: "90d" },
];

const Analytics: React.FC = () => {
	const [days, setDays] = useState(7);
	const [site, setSite] = useState<string>("all"); // "all" | "production" | "preview"
	const { data, loading, reload } = useAnalyticsOverview(days);

	const instances = data?.instances ?? [];
	const selected = useMemo(() => (site === "all" ? instances : instances.filter((i) => i.target === site)), [instances, site]);
	const view = useMemo(() => buildView(selected), [selected]);

	const windowLabel = `${days} jour${days > 1 ? "s" : ""}`;

	return (
		<div className="min-h-full overflow-x-hidden bg-slate-50 px-4 py-5 sm:px-6 sm:py-6 lg:px-8">
			<div className="w-full space-y-5">
			{/* Header: title + site selector + window toggle + refresh */}
			<div className="flex flex-wrap items-center justify-between gap-3">
				<div className="inline-flex items-center gap-2 rounded-lg border border-slate-200 bg-white px-3 py-1.5 text-sm font-semibold text-slate-800 shadow-sm">
					<FiCloud className="h-4 w-4 text-sky-500" /> Web Analytics
				</div>
				<div className="flex flex-wrap items-center gap-2">
					<select
						value={site}
						onChange={(e) => setSite(e.target.value)}
						className="rounded-lg border border-slate-200 bg-white px-3 py-1.5 text-sm font-medium text-slate-700 shadow-sm focus:outline-none focus:ring-2 focus:ring-sky-200"
					>
						<option value="all">Tous les sites</option>
						{instances.map((i) => (
							<option key={i.target} value={i.target}>
								{TARGET_LABEL[i.target] ?? i.target}
							</option>
						))}
					</select>
					<div className="flex items-center gap-0.5 rounded-lg border border-slate-200 bg-white p-0.5 shadow-sm">
						{WINDOWS.map((w) => (
							<button
								key={w.days}
								type="button"
								onClick={() => setDays(w.days)}
								className={`rounded-md px-3 py-1 text-xs font-semibold transition ${
									days === w.days ? "bg-[#0095DB] text-white" : "text-slate-500 hover:bg-slate-100"
								}`}
							>
								{w.label}
							</button>
						))}
					</div>
					<button
						type="button"
						onClick={reload}
						className="inline-flex items-center gap-1.5 rounded-lg border border-slate-200 bg-white px-3 py-1.5 text-sm font-medium text-slate-600 shadow-sm hover:bg-slate-50"
					>
						<FiRefreshCw className={`h-3.5 w-3.5 ${loading ? "animate-spin" : ""}`} /> Refresh
					</button>
				</div>
			</div>

			{!loading && (!data || !data.configured || !view.configured) ? (
				<Panel className="p-8">
					<div className="flex min-h-[200px] flex-col items-center justify-center gap-2 text-center">
						<span className="flex h-11 w-11 items-center justify-center rounded-full bg-sky-50 text-sky-500">
							<FiCloud className="h-5 w-5" />
						</span>
						<p className="text-sm font-semibold text-slate-700">Connecter Cloudflare Web Analytics</p>
						<p className="max-w-md text-xs text-slate-500">
							{data?.reason || view.reason || "Web Analytics non configuré."} Renseignez{" "}
							<code className="rounded bg-slate-100 px-1">CloudflareAnalytics:AccountId</code> et un token avec la portée Account Analytics:Read.
						</p>
						<a
							href="https://dash.cloudflare.com/?to=/:account/web-analytics"
							target="_blank"
							rel="noreferrer"
							className="mt-1 inline-flex items-center gap-1 text-xs font-semibold text-sky-600 hover:text-sky-700 hover:underline"
						>
							Ouvrir Cloudflare Web Analytics <FiExternalLink className="h-3 w-3" />
						</a>
					</div>
				</Panel>
			) : (
				<>
					{/* KPI tiles : the two volumes carry their trend against the previous window of equal length. */}
					<div className="grid grid-cols-2 gap-4 lg:grid-cols-4">
						<Kpi
							label="Visites"
							value={loading ? "…" : compact.format(view.visits)}
							color={VISITS_COLOR}
							trend={loading || view.previousVisits <= 0 ? null : { current: view.visits, previous: view.previousVisits }}
							hint={`sur ${windowLabel} · sessions réelles, sans robots`}
						/>
						<Kpi
							label="Pages vues"
							value={loading ? "…" : compact.format(view.pageViews)}
							color="#16A34A"
							trend={loading || view.previousPageViews <= 0 ? null : { current: view.pageViews, previous: view.previousPageViews }}
							hint={loading || view.visits === 0 ? undefined : `${(view.pageViews / view.visits).toLocaleString("fr-FR", { maximumFractionDigits: 1 })} pages par visite`}
						/>
						<Kpi label="Pages distinctes" value={loading ? "…" : full.format(view.pageCount)} color="#6366F1" hint="pages ayant reçu au moins une vue" />
						<Kpi label="Pays" value={loading ? "…" : full.format(view.countries.length)} color="#0EA5E9" hint={`fenêtre de ${windowLabel}`} />
					</div>

					{/* Visits & page views chart */}
					<Panel className="p-5">
						<div className="mb-2 flex items-center justify-between">
							<p className="text-[11px] font-semibold uppercase tracking-wide text-slate-400">Visites &amp; pages vues</p>
							<Legend />
						</div>
						<DualAreaChart series={view.series} />
					</Panel>

					{/* Most viewed pages (per page / per section) + where the traffic comes from. */}
					<div className="grid gap-4 lg:grid-cols-[1.9fr_1fr]">
						<PagesInsights pages={view.pages} pageCount={view.pageCount} host={view.host} days={days} loading={loading} />
						<TrafficSources referrers={view.referrers} loading={loading} />
					</div>

					{/* Locations map + country leaderboard */}
					<div className="grid gap-4 lg:grid-cols-[1.9fr_1fr]">
						<Panel title="Localisations" className="p-0">
							<div className="p-4">
								<div className="rounded-lg border border-slate-100 bg-slate-50/70 p-2">
									<WorldTrafficMap countries={view.countries} className="h-auto w-full" />
								</div>
							</div>
						</Panel>
						<Panel title="Pays" className="p-0">
							<div className="px-5 pb-4 pt-2">
								{view.countries.length === 0 ? (
									<p className="py-10 text-center text-xs text-slate-400">Aucune donnée géographique pour cette période.</p>
								) : (
									<ul className="divide-y divide-slate-50">
										{view.countries.slice(0, 8).map((c) => (
											<CountryRow key={c.code} country={c} max={view.countries[0]?.count ?? 0} />
										))}
									</ul>
								)}
							</div>
						</Panel>
					</div>

					{/* Browser / OS / device breakdowns */}
					<div className="grid gap-4 lg:grid-cols-3">
						<BreakdownPanel title="Navigateurs" items={view.browsers} />
						<BreakdownPanel title="Systèmes d'exploitation" items={view.operatingSystems} />
						<BreakdownPanel title="Appareils" items={view.devices} />
					</div>
				</>
			)}
			</div>
		</div>
	);
};

export default Analytics;
